// Which project's Dennis conversation the dashboard's centre pane is bound to.
//
// One module owns the key because TWO surfaces act on it: `Dashboard.js`, which
// reads it at mount and writes it on every bind, and `/projects`' "New project"
// button, which must RELEASE it — that button navigates to `/dashboard`, and a
// still-bound id means it lands on the open conversation instead of the composer,
// which is the whole complaint it was supposed to answer.
//
// A shared module rather than router state: `Dashboard.test.js`'s
// `react-router-dom` mock deliberately exposes only `useNavigate`, and every other
// piece of rail state (`hidenn.dashSections`, `…SidebarWidth`, `…RailCollapsed`)
// already persists this way.
//
// **sessionStorage, not localStorage** — the store is what encodes the intent here.
// localStorage is shared by every tab on the origin, so opening the product in a NEW
// tab dropped you into whichever conversation was last open instead of offering a new
// project. sessionStorage is per-tab: a fresh tab starts empty and lands on the
// composer, while a RELOAD of the same tab still resumes the thread you were reading
// (which is why this is not simply dropped). The other rail keys stay in localStorage
// deliberately — a sidebar width is a preference and belongs to the user, whereas
// "which conversation am I looking at" belongs to the tab.
//
// Every accessor swallows its error. Storage throws in private mode, and the only
// consequence is that the binding does not survive a reload — never a reason to fail
// the render or the navigation.

export const THREAD_KEY = 'hidenn.dashConversation';

/** The bound project id, or null. */
export function readBoundConversation() {
  try {
    // Drop the key this used to be written to. A build before the move left one
    // behind, and it is dead either way — deliberately NOT adopted into
    // sessionStorage, since resuming in a fresh tab is the behaviour being removed.
    localStorage.removeItem(THREAD_KEY);
    return sessionStorage.getItem(THREAD_KEY) || null;
  } catch {
    return null;
  }
}

/** Bind (a project id) or release (null/'' — the two are the same intent). */
export function writeBoundConversation(projectId) {
  try {
    if (projectId) sessionStorage.setItem(THREAD_KEY, projectId);
    else sessionStorage.removeItem(THREAD_KEY);
  } catch {
    /* private mode — the thread just won't survive a reload */
  }
}

/** Release, so the next dashboard mount shows the composer. */
export function clearBoundConversation() {
  writeBoundConversation(null);
}
